<template>
  <b-card no-body class="cut-create">
    <div class="cut-create__header">
      <div class="cut-create__title">
        <b-button variant="link" class="list-view-icon-btn" title="Volver a los cortes" aria-label="Volver a los cortes" @click="$emit('cancel')">
          <AppIcon name="arrow_back" />
        </b-button>
        <div>
          <h4 class="mb-0">Nuevo corte</h4>
          <div class="cut-create__meta">Elegí los pedidos cerrados que se envían juntos al depósito.</div>
        </div>
      </div>
    </div>

    <div class="list-view-toolbar__filters cut-create__filters">
      <div class="list-view-filter cut-create__filter-date">
        <label class="filter-label">Pedidos recibidos hasta</label>
        <b-form-input v-model="cutoffAt" type="datetime-local" size="sm" @change="applyFilters" />
      </div>
      <div class="list-view-filter cut-create__filter-search">
        <label class="filter-label">Cliente o nº de pedido</label>
        <b-form-input v-model="search" size="sm" placeholder="Todos" @keyup.enter.native="applyFilters" />
      </div>
      <div class="list-view-filter cut-create__filter-option">
        <label class="filter-label">Vendedor</label>
        <b-form-select v-model="sellerId" size="sm" :options="sellerOptions" @change="onSellerChange" />
      </div>
      <div class="list-view-filter cut-create__filter-option">
        <label class="filter-label">Zona</label>
        <b-form-select v-model="zoneId" size="sm" :options="zoneOptions" @change="applyFilters" />
      </div>
      <div class="list-view-filter cut-create__filter-option">
        <label class="filter-label">Ciudad</label>
        <b-form-select v-model="city" size="sm" :options="cityOptions" @change="applyFilters" />
      </div>
      <b-button variant="outline-dark" size="sm" class="cut-create__filter-btn" title="Aplicar filtros" aria-label="Aplicar filtros" :disabled="loading" @click="applyFilters">
        <AppIcon name="filter" />
      </b-button>
    </div>

    <div v-if="blocked.length" class="app-alert app-alert--warning cut-create__blocked">
      <span class="app-alert__icon"><AppIcon name="warning" /></span>
      <span class="app-alert__content">
        {{ blocked.length }} {{ blocked.length === 1 ? 'pedido cerrado no puede' : 'pedidos cerrados no pueden' }} entrar a un corte hasta completar sus datos.
        <b-button variant="link" class="list-view-link p-0 ml-1" @click="showBlocked = !showBlocked">{{ showBlocked ? 'Ocultar' : 'Ver cuáles' }}</b-button>
        <ul v-if="showBlocked" class="cut-create__blocked-list">
          <li v-for="order in blocked" :key="order.id"><strong>#{{ order.id }}</strong> {{ order.customer_name || 'Sin cliente' }} — {{ order.reason }}</li>
        </ul>
      </span>
    </div>

    <div class="cut-create__select-bar">
      <b-form-checkbox v-if="items.length" :checked="allPageSelected" switch @change="togglePage">
        {{ allPageSelected ? 'Quitar esta página' : 'Seleccionar esta página' }}
      </b-form-checkbox>
      <b-button v-if="total > items.length" variant="link" class="list-view-link p-0" @click="toggleAll">
        {{ allSelected ? 'Quitar la selección total' : 'Seleccionar los ' + total + ' pedidos encontrados' }}
      </b-button>
    </div>

    <b-table class="app-table cut-create__table mb-0" :items="items" :fields="fields" :busy="loading"
             :tbody-tr-class="rowClass" :stacked="isMobile ? 'md' : false"
             sticky-header="56vh" responsive small hover primary-key="id" @row-clicked="toggle"
             v-if="items.length || loading">
      <template v-slot:table-busy>
        <div class="text-center my-2"><b-spinner small class="align-middle" /> <strong>Buscando pedidos…</strong></div>
      </template>
      <template v-slot:cell(select)="data">
        <span class="cut-create__check" role="checkbox" :aria-checked="isSelected(data.item.id) ? 'true' : 'false'" @click.stop="toggle(data.item)">
          <AppIcon :name="isSelected(data.item.id) ? 'check_box' : 'check_box_outline_blank'" :fill="isSelected(data.item.id)"
                   :class="isSelected(data.item.id) ? 'text-dark' : 'text-secondary'" />
        </span>
      </template>
      <template v-slot:cell(id)="data"><strong>#{{ data.item.id }}</strong></template>
      <template v-slot:cell(customer_name)="data">{{ data.item.customer_name || 'Sin cliente' }}</template>
      <template v-slot:cell(seller_name)="data">{{ data.item.seller_name || 'Sin vendedor' }}</template>
      <template v-slot:cell(place)="data">{{ data.item.zone_name || 'Sin zona' }} › {{ data.item.city || 'Sin ciudad' }}</template>
      <template v-slot:cell(created_at)="data">{{ formatDate(data.item.created_at) }}</template>
      <template v-slot:cell(total)="data">{{ formatMoney(data.item.total) }}</template>
    </b-table>
    <EmptyState v-else icon="assignment" title="No hay pedidos disponibles"
                description="No hay pedidos cerrados sin corte hasta esa hora. Cambiá la hora de corte o los filtros." />

    <div class="app-table-footer cut-create__footer">
      <span class="list-view-toolbar__count">{{ total }} pedidos disponibles</span>
      <b-pagination v-if="total > perPage" v-model="page" :total-rows="total" :per-page="perPage" size="sm" pills class="mb-0" @input="load($event)" />
    </div>

    <div v-if="selectedCount" class="cut-create__confirm">
      <span><strong>{{ selectedCount }}</strong> {{ selectedCount === 1 ? 'pedido seleccionado' : 'pedidos seleccionados' }}</span>
      <b-button variant="dark" size="sm" :disabled="busy" @click="create">
        <AppIcon name="inventory_2" class="mr-1" /> Crear corte
      </b-button>
    </div>
  </b-card>
</template>

<script>
import serviceAPI from '../services'
import Error from '@/handler/error'

export default {
  name: 'OrderCutCreate',
  props: { isMobile: { type: Boolean, default: false } },
  data() {
    return {
      cutoffAt: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      search: '', sellerId: null, zoneId: null, city: '',
      options: { sellers: [], zones: [], cities: [] },
      blocked: [], showBlocked: false,
      items: [], page: 1, total: 0, perPage: 50, loading: false, busy: false,
      selectedIds: [], allSelected: false, excludedIds: [],
      fields: [
        { key: 'select', label: '', class: 'text-center', thStyle: { width: '40px' } },
        { key: 'id', label: 'Pedido' },
        { key: 'customer_name', label: 'Cliente' },
        { key: 'seller_name', label: 'Vendedor' },
        { key: 'place', label: 'Zona › Ciudad' },
        { key: 'created_at', label: 'Recibido' },
        { key: 'total', label: 'Total', class: 'text-right' },
      ],
    }
  },
  computed: {
    sellerOptions() { return [{ value: null, text: 'Todos' }].concat(this.options.sellers.map(item => ({ value: item.id, text: item.name }))) },
    zoneOptions() {
      const zones = this.options.zones.filter(item => !this.sellerId || Number(item.sellers_id) === Number(this.sellerId))
      return [{ value: null, text: 'Todas' }].concat(zones.map(item => ({ value: item.id, text: item.name })))
    },
    cityOptions() { return [{ value: '', text: 'Todas' }].concat(this.options.cities.map(city => ({ value: city, text: city }))) },
    selectedCount() { return this.allSelected ? Math.max(0, this.total - this.excludedIds.length) : this.selectedIds.length },
    allPageSelected() { return !!this.items.length && this.items.every(order => this.isSelected(order.id)) },
  },
  created() {
    this.loadOptions()
    this.load(1)
  },
  methods: {
    formatDate(value) {
      return value ? new Date(String(value).replace(' ', 'T')).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'
    },
    formatMoney(value) { return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value) || 0) },
    filters() {
      return {
        cutoff_at: this.cutoffAt ? this.cutoffAt.replace('T', ' ') + ':00' : null,
        search: this.search || null,
        seller_id: this.sellerId,
        zone_id: this.zoneId,
        city: this.city || null,
      }
    },
    async loadOptions() {
      try {
        const response = await serviceAPI.filtrosPedidosParaCorte()
        this.options = { sellers: response.data.sellers || [], zones: response.data.zones || [], cities: response.data.cities || [] }
        this.blocked = response.data.blocked || []
      } catch(error) {
        this.$awn.alert(Error.showError(error))
      }
    },
    async load(page = 1) {
      this.loading = true
      try {
        const response = await serviceAPI.pedidosParaCorte({ page, ...this.filters() })
        this.items = response.data.data || []
        this.total = response.data.total || 0
        this.perPage = response.data.per_page || 50
        this.page = response.data.current_page || page
      } catch(error) {
        this.$awn.alert(Error.showError(error))
      } finally {
        this.loading = false
      }
    },
    // Una selección hecha con otros filtros ya no se ve: se limpia para no crear un corte con pedidos ocultos.
    applyFilters() {
      this.selectedIds = []
      this.excludedIds = []
      this.allSelected = false
      return this.load(1)
    },
    onSellerChange() {
      this.zoneId = null
      return this.applyFilters()
    },
    isSelected(id) { return this.allSelected ? !this.excludedIds.includes(id) : this.selectedIds.includes(id) },
    toggle(item) {
      const id = item.id
      if(this.allSelected) {
        this.excludedIds = this.excludedIds.includes(id) ? this.excludedIds.filter(value => value !== id) : this.excludedIds.concat(id)
      } else {
        this.selectedIds = this.selectedIds.includes(id) ? this.selectedIds.filter(value => value !== id) : this.selectedIds.concat(id)
      }
    },
    togglePage() {
      const pageIds = this.items.map(order => order.id)
      const select = !this.allPageSelected
      if(this.allSelected) {
        this.excludedIds = select ? this.excludedIds.filter(id => !pageIds.includes(id)) : Array.from(new Set(this.excludedIds.concat(pageIds)))
      } else {
        this.selectedIds = select ? Array.from(new Set(this.selectedIds.concat(pageIds))) : this.selectedIds.filter(id => !pageIds.includes(id))
      }
    },
    toggleAll() {
      this.allSelected = !this.allSelected
      this.selectedIds = []
      this.excludedIds = []
    },
    rowClass(item, type) { return type === 'row' && item && this.isSelected(item.id) ? 'app-table-row-active' : '' },
    async create() {
      this.busy = true
      try {
        const response = await serviceAPI.crearCorte({
          cutoff_at: this.filters().cutoff_at,
          order_ids: this.selectedIds,
          select_all: this.allSelected,
          excluded_order_ids: this.excludedIds,
          filters: this.filters(),
        })
        this.$awn.success('Corte creado con ' + this.selectedCount + ' pedidos')
        this.$emit('created', response.data.id)
      } catch(error) {
        this.$awn.alert(Error.showError(error))
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.cut-create__header { padding: 0.9rem 1.1rem 0.4rem; }
.cut-create__title { display: flex; align-items: flex-start; gap: 0.5rem; }
.cut-create__title h4 { font-size: 1.15rem; font-weight: 700; }
.cut-create__meta { margin-top: 0.15rem; font-size: 0.78rem; color: var(--app-color-text-muted, #687684); }
.cut-create__filters { padding: 0.5rem 1.1rem 0.8rem; border-bottom: 1px solid var(--app-color-border, #dee2e6); }
.cut-create__filter-date { width: 210px; }
.cut-create__filter-search { flex: 1 1 180px; max-width: 240px; }
.cut-create__filter-option { flex: 1 1 140px; max-width: 190px; }
.cut-create__filter-btn { width: 31px; height: 31px; padding: 0; flex-shrink: 0; }
.cut-create__blocked { margin: 0.75rem 1.1rem 0; }
.cut-create__blocked-list { margin: 0.4rem 0 0; padding-left: 1rem; max-height: 160px; overflow-y: auto; font-size: 0.78rem; }
.cut-create__select-bar { display: flex; align-items: center; gap: 1rem; min-height: 2.6rem; padding: 0.4rem 1.1rem; }
.cut-create__table >>> tbody tr { cursor: pointer; }
.cut-create__check { display: inline-flex; padding: 0.15rem; font-size: 1.25rem; cursor: pointer; }
.cut-create__footer { padding: 0.6rem 1.1rem; }
.cut-create__confirm { position: sticky; bottom: 0; z-index: 3; display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0.6rem 1.1rem; color: var(--app-color-on-primary, #fff); background: var(--app-color-text, #1c2833); }
.cut-create__confirm .btn-dark { border-color: var(--app-color-on-primary, #fff); }
@media (max-width: 767.98px) {
  .cut-create__filter-date, .cut-create__filter-search, .cut-create__filter-option { flex: 1 1 100%; width: auto; max-width: none; }
}
</style>
